import { get, set, del } from "idb-keyval";
import { createAsyncStoragePersister } from "@tanstack/query-async-storage-persister";

import { withStorageTimeout } from "@/lib/clientStorage";

/** Default key used by @tanstack/query-async-storage-persister. */
export const QUERY_PERSIST_KEY = "REACT_QUERY_OFFLINE_CACHE";

const PERSIST_GET_TIMEOUT_MS = 3_000;

/** IndexedDB-backed persister for React Query offline cache. */
export const asyncStoragePersister = createAsyncStoragePersister({
  key: QUERY_PERSIST_KEY,
  storage: {
    getItem: async (key) => {
      try {
        const value = await withStorageTimeout(get(key), PERSIST_GET_TIMEOUT_MS);
        return value ?? null;
      } catch {
        return null;
      }
    },
    setItem: async (key, value) => {
      try {
        await set(key, value);
      } catch {
        /* ignore — offline cache is best-effort */
      }
    },
    removeItem: async (key) => {
      try {
        await del(key);
      } catch {
        /* ignore */
      }
    },
  },
});

/** Remove persisted React Query cache (e.g. when revoking optional storage). */
export async function clearPersistedQueryCache(): Promise<void> {
  try {
    await del(QUERY_PERSIST_KEY);
  } catch {
    /* ignore */
  }
}

/** Deploy buster — invalidates persisted cache when a new version ships. */
export function queryPersistBuster(): string {
  const sha =
    import.meta.env.VITE_COMMIT_SHA?.trim() ||
    import.meta.env.VITE_VERCEL_GIT_COMMIT_SHA?.trim();
  return sha || "dev";
}
